import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Play, Star, GraduationCap, X } from "lucide-react";

const SUNANDHI_VIDEO = "https://customer-assets.emergentagent.com/job_skill-forward-2/artifacts/wbgj4ejj_Snapchat-59908890.mp4";
const KARAN_VIDEO = "https://customer-assets.emergentagent.com/job_skill-forward-2/artifacts/fobymmeq_Untitled%20Project%204.m4v";
const DURGHESH_VIDEO = "https://customer-assets.emergentagent.com/job_skill-forward-2/artifacts/n901nq5j_Untitled%20Project%2013.m4v";
const DURGHESH_POSTER = "/testimonials/durghesh-poster.jpg";

const TESTIMONIALS = [
  {
    id: "sunandhi",
    name: "Sunandhi Khajuria",
    role: "College Student",
    package: "Premium Package",
    quote: "Got clarity and confidence to start my freelancing journey while managing college.",
    video: SUNANDHI_VIDEO,
    poster: null,
  },
  {
    id: "karan",
    name: "Karan Chavan",
    role: "Video Editor & Freelancer",
    package: "Premium Package",
    quote: "The Premium Package gave me the guidance, mentorship, and community support I needed to grow as a video editor and freelancer.",
    video: KARAN_VIDEO,
    poster: null,
  },
  {
    id: "durghesh",
    name: "Durghesh Sharma",
    role: "School Student",
    package: "Basic Package",
    quote: "The 1% Community Club changed how I think about skills — now I'm working while studying.",
    video: DURGHESH_VIDEO,
    poster: DURGHESH_POSTER,
  },
  {
    id: "rohit",
    name: "Rohit Sharma",
    package: "Basic Package",
    quote: "From zero design knowledge to landing my first ₹15K Fiverr client in 6 weeks.",
    video: null,
    poster: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=80",
  },
  {
    id: "aisha",
    name: "Aisha Verma",
    package: "Premium Package",
    quote: "The 1% Club community changed how I think about content — now growing on YouTube.",
    video: null,
    poster: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=600&q=80",
  },
  {
    id: "kunal",
    name: "Kunal Mehta",
    package: "Basic Package",
    quote: "Picked Video Editing + Freelancing — already editing reels for 2 paying brands.",
    video: null,
    poster: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=600&q=80",
  },
  {
    id: "ananya",
    name: "Ananya Iyer",
    package: "Premium Package",
    quote: "AI course unlocked a totally new skill set — I now offer prompt engineering to startups.",
    video: null,
    poster: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=600&q=80",
  },
  {
    id: "vihaan",
    name: "Vihaan Kapoor",
    package: "E-Lite Package",
    quote: "Started with just the Content Creation course — first 10K Instagram followers in 90 days.",
    video: null,
    poster: "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&w=600&q=80",
  },
];

function Stars({ rating = 5 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3 w-3 ${i <= rating ? "fill-amber-400 text-amber-400" : "text-slate-600"}`}
        />
      ))}
    </div>
  );
}

function TestimonialCard({ t, onOpen }) {
  const videoRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const hasVideo = !!t.video;
  const hasEmbed = !!t.embed_url;

  const handleEnter = () => {
    setHovered(true);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };
  const handleLeave = () => {
    setHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const packageStyles = {
    "Premium Package": "from-[#06B6D4] to-[#2563EB] text-white",
    "Basic Package": "from-[#2563EB] to-[#1E3A8A] text-white",
    "E-Lite Package": "from-slate-700 to-slate-900 text-white",
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 240, damping: 22 }}
      data-testid={`testimonial-card-${t.id}`}
      className="snap-start shrink-0 w-[280px] sm:w-[300px] flex flex-col rounded-3xl border border-white/10 bg-gradient-to-br from-[#0F172A]/95 via-[#0F172A]/90 to-[#1E3A8A]/40 text-white backdrop-blur-xl shadow-[0_15px_50px_-20px_rgba(15,23,42,0.6)] hover:shadow-[0_25px_60px_-20px_rgba(6,182,212,0.35)] hover:border-[#06B6D4]/40 transition-all duration-300 overflow-hidden"
    >
      {/* Text block — fixed min-height keeps the media start position identical across cards */}
      <div className="p-4 pb-3 min-h-[170px] flex flex-col">
        <span
          className={`self-start inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold ${packageStyles[t.package] || packageStyles["E-Lite Package"]}`}
        >
          <GraduationCap className="h-3 w-3" />
          {t.package} Student
        </span>
        <h3 className="mt-3 font-display text-lg font-semibold tracking-tight text-white line-clamp-1">
          {t.name}
        </h3>
        {/* Reserve role row even when missing so media tops align across all cards */}
        <div className="mt-0.5 h-[16px] text-[11px] uppercase tracking-[0.18em] font-semibold text-[#06B6D4]">
          {t.role || "\u00A0"}
        </div>
        <p className="mt-1.5 text-[13px] leading-snug text-slate-300 line-clamp-3">
          &ldquo;{t.quote}&rdquo;
        </p>
      </div>

      <div
        className="relative mx-4 aspect-[9/16] rounded-2xl overflow-hidden bg-[#020617] border border-white/10 cursor-pointer group/video"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={() => (hasVideo || hasEmbed) && onOpen(t)}
        data-testid={`testimonial-video-${t.id}`}
      >
        {hasVideo ? (
          <video
            ref={videoRef}
            src={t.video}
            muted
            playsInline
            loop
            preload="metadata"
            poster={t.poster || undefined}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : t.poster ? (
          <img
            src={t.poster}
            alt={`${t.name} portrait`}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#1E3A8A]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/80 via-transparent to-transparent pointer-events-none" />
        <button
          type="button"
          aria-label={hasVideo ? `Play ${t.name}'s testimonial` : "Coming soon"}
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
            hovered && hasVideo ? "opacity-0" : "opacity-100"
          }`}
        >
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/95 text-[#0F172A] shadow-[0_8px_24px_-6px_rgba(0,0,0,0.5)] group-hover/video:scale-110 transition-transform duration-300">
            <Play className="h-5 w-5 fill-current" />
          </span>
        </button>
        {!hasVideo && !hasEmbed && (
          <span className="absolute bottom-2 left-2 right-2 inline-flex items-center justify-center rounded-full bg-black/50 backdrop-blur text-white text-[10px] uppercase tracking-[0.18em] font-semibold py-1">
            Video story coming soon
          </span>
        )}
      </div>

      <div className="px-4 py-3 flex items-center justify-between">
        <Stars rating={t.rating} />
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
          Verified
        </span>
      </div>
    </motion.div>
  );
}

function VideoModal({ open, t, onClose }) {
  if (!open || !t) return null;
  return (
    <div
      data-testid="testimonial-modal"
      onClick={onClose}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-md w-full"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="testimonial-modal-close"
          className="absolute -top-3 -right-3 h-9 w-9 inline-flex items-center justify-center rounded-full bg-white text-[#0F172A] shadow-lg hover:scale-105 transition-transform z-10"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="aspect-[9/16] rounded-3xl overflow-hidden bg-black">
          {t.embed_url ? (
            <iframe
              src={t.embed_url}
              title={`${t.name} testimonial`}
              allow="autoplay; fullscreen"
              allowFullScreen
              data-testid="testimonial-modal-frame"
              className="w-full h-full"
            />
          ) : (
            <video
              src={t.video}
              controls
              autoPlay
              playsInline
              poster={t.poster || undefined}
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="mt-4 text-center text-white">
          <div className="font-display text-lg font-semibold">{t.name}</div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#06B6D4] font-semibold mt-1">
            {t.package} Student
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SuccessStories() {
  const scrollerRef = useRef(null);
  const [openVideo, setOpenVideo] = useState(null);
  const [items, setItems] = useState(TESTIMONIALS);

  useEffect(() => {
    // Published testimonials are admin-managed; the static list is the fallback.
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setItems(data);
      })
      .catch(() => {});
  }, []);

  const scrollBy = (dir) => {
    if (!scrollerRef.current) return;
    const amount = 320 * dir;
    scrollerRef.current.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <section
      id="stories"
      data-testid="section-stories"
      className="py-14 sm:py-20 bg-gradient-to-b from-slate-100/60 dark:from-white/[0.02] to-transparent overflow-x-clip"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              Student Success Stories
            </span>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl tracking-tighter font-semibold text-[#0F172A] dark:text-white text-balance leading-[1.05]">
              Hear it from our students.
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Real students, real journeys — tap any video to watch the full story.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              data-testid="stories-prev"
              className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-white/10 transition-colors"
              aria-label="Previous testimonials"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              data-testid="stories-next"
              className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-white/10 transition-colors"
              aria-label="Next testimonials"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          data-testid="stories-scroller"
          className="mt-10 flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-2 px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {items.map((t) => (
            <TestimonialCard key={t.id} t={t} onOpen={setOpenVideo} />
          ))}
        </div>
      </div>

      <VideoModal open={!!openVideo} t={openVideo} onClose={() => setOpenVideo(null)} />
    </section>
  );
}
